import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerRenglonesVenta, eliminarVenta } from '../services/balanceService';
import { editarVentaCredito } from '../services/porCobrarService';
import { obtenerClientes, crearCliente } from '../services/clientesService';
import { CANALES } from '../utils/canales';
import { precioEfectivo } from '../utils/precios';
import { fechaLocal, timestampParaFecha } from '../utils/fecha';
import { mensajeAmigable } from '../utils/errores';
import { numeroSeguro } from '../utils/validacion';
import CampoNumero from './CampoNumero';
import { useToast } from '../context/ToastContext';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Editar TODO de una venta a crédito (items, cliente, canal, fecha, descuento,
// concepto, nota, envío). Preserva los abonos ya hechos (los reaplica).
export default function EditarVentaCreditoModal({ cxc, productos, onCerrar, onGuardado }) {
  const { mostrarToast } = useToast();
  const referencia = cxc.ticket_id;
  const abonado = Number(cxc.monto_pagado || 0);
  const [items, setItems] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [descuento, setDescuento] = useState('');
  const [envio, setEnvio] = useState('');
  const [concepto, setConcepto] = useState('');
  const [nota, setNota] = useState('');
  const [canal, setCanal] = useState('');
  const [fechaV, setFechaV] = useState(fechaLocal());
  const [nuevoCli, setNuevoCli] = useState(false);
  const [cliNombre, setCliNombre] = useState('');
  const [cliTel, setCliTel] = useState('');
  const [guardandoCli, setGuardandoCli] = useState(false);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);

  const borrarVenta = async () => {
    setGuardando(true);
    try {
      await eliminarVenta(referencia);
      mostrarToast('Venta a crédito eliminada. Stock y abonos revertidos ✅', 'exito');
      onGuardado && onGuardado(); onCerrar();
    } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); } finally { setGuardando(false); }
  };

  useEffect(() => {
    let activo = true;
    Promise.all([obtenerRenglonesVenta(referencia), obtenerClientes()])
      .then(([renglones, cli]) => {
        if (!activo) return;
        if (!renglones.length) { setError('No se encontró la venta.'); return; }
        setClientes(cli);
        setClienteId(cxc.cliente_id || renglones[0].cliente_id || '');
        setConcepto(cxc.concepto || renglones[0].concepto || '');
        setNota(renglones[0].nota || '');
        setCanal(renglones[0].canal || '');
        if (renglones[0].vendido_en) setFechaV(fechaLocal(new Date(renglones[0].vendido_en)));

        const its = renglones.map((r) => {
          const prod = productos.find((p) => p.id === r.producto_id);
          const precio = prod ? precioEfectivo(prod) : Number(r.precio_unitario || 0);
          const stockMax = (prod ? (prod.stock || 0) : 0) + (r.cantidad || 0);
          return { producto_id: r.producto_id, codigo: r.codigo_producto, nombre: r.nombre_producto, precio, cantidad: r.cantidad || 1, stockMax };
        });
        setItems(its);

        const listaTotal = its.reduce((s, i) => s + i.precio * i.cantidad, 0);
        const cobradoProd = renglones.reduce((s, r) => s + Number(r.total || 0), 0);
        const desc = Math.round(listaTotal - cobradoProd);
        if (desc > 0) setDescuento(String(desc));
        // Envío = lo que se le sumó a la deuda por encima de los productos.
        const env = Math.round((Number(cxc.monto_total || 0) - cobradoProd) * 100) / 100;
        if (env > 0) setEnvio(String(env));
      })
      .catch((e) => activo && setError(mensajeAmigable(e)));
    return () => { activo = false; };
  }, [referencia, productos, cxc]);

  const cambiar = (id, delta) => setItems((prev) => prev.map((i) => {
    if (i.producto_id !== id) return i;
    const nueva = i.cantidad + delta;
    if (nueva < 1) return i;
    if (nueva > i.stockMax) { mostrarToast(`Máximo ${i.stockMax} disponibles`, 'error'); return i; }
    return { ...i, cantidad: nueva };
  }));
  const quitar = (id) => setItems((prev) => prev.filter((i) => i.producto_id !== id));

  const subtotal = useMemo(() => (items || []).reduce((s, i) => s + i.precio * i.cantidad, 0), [items]);
  const desc = Math.min(numeroSeguro(descuento), subtotal);
  const envioNum = Math.max(numeroSeguro(envio), 0);
  const total = subtotal - desc + envioNum; // total de la deuda (productos − desc + envío)

  const guardarClienteRapido = async () => {
    if (!cliNombre.trim()) { mostrarToast('Escribe el nombre del cliente', 'error'); return; }
    setGuardandoCli(true);
    try {
      const nuevo = await crearCliente({ nombre: cliNombre, telefono: cliTel });
      setClientes((prev) => [...prev, nuevo].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')));
      setClienteId(nuevo.id);
      setNuevoCli(false); setCliNombre(''); setCliTel('');
    } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); } finally { setGuardandoCli(false); }
  };

  const guardar = async () => {
    if (!items || items.length === 0) { mostrarToast('La venta debe tener al menos un producto', 'error'); return; }
    if (!clienteId) { mostrarToast('Elige o crea el cliente (es venta a crédito)', 'error'); return; }
    if (total < abonado - 0.01) { mostrarToast(`El total no puede ser menor a lo ya abonado (${fmt(abonado)})`, 'error', 5000); return; }
    setGuardando(true);
    try {
      await editarVentaCredito(referencia, {
        items, clienteId, descuento: desc, concepto: concepto || null, nota: nota || null,
        canal: canal || null, fecha: timestampParaFecha(fechaV), envio: envioNum,
      });
      mostrarToast('Venta a crédito actualizada ✅', 'exito');
      onGuardado && onGuardado(); onCerrar();
    } catch (e) {
      const msg = (e?.message || '').includes('MONTO_MAYOR_AL_SALDO')
        ? 'El total quedó menor a lo ya abonado. Ajustá el monto.'
        : (e?.message || '').includes('STOCK_INSUFICIENTE') ? 'No hay stock suficiente.' : mensajeAmigable(e);
      mostrarToast(msg, 'error');
    } finally { setGuardando(false); }
  };

  const input = { width: '100%', padding: '9px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '16px', padding: '20px', width: '460px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, color: '#B45309' }}>✏️ Editar venta a crédito</h3>
          <button onClick={onCerrar} disabled={guardando} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer' }}>✕</button>
        </div>

        {error && <p style={{ color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>}
        {!items && !error && <p style={{ color: '#6B7280', textAlign: 'center', padding: '20px' }}>⏳ Cargando venta...</p>}

        {items && (
          <>
            {items.map((i) => (
              <div key={i.producto_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 600 }}>{i.nombre}</p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>{i.codigo ? `${i.codigo} · ` : ''}{fmt(i.precio)} c/u · máx {i.stockMax}</p>
                </div>
                <button onClick={() => cambiar(i.producto_id, -1)} disabled={guardando} style={{ width: '26px', height: '26px', borderRadius: '6px', border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer', fontWeight: 'bold' }}>−</button>
                <span style={{ minWidth: '20px', textAlign: 'center', fontWeight: 600 }}>{i.cantidad}</span>
                <button onClick={() => cambiar(i.producto_id, 1)} disabled={guardando} style={{ width: '26px', height: '26px', borderRadius: '6px', border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer', fontWeight: 'bold' }}>+</button>
                <span style={{ minWidth: '66px', textAlign: 'right', fontWeight: 'bold', color: 'var(--color-primario,#DB2777)', fontSize: '13px' }}>{fmt(i.precio * i.cantidad)}</span>
                <button onClick={() => quitar(i.producto_id)} disabled={guardando} title="Quitar" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '15px' }}>✕</button>
              </div>
            ))}

            <div style={{ marginTop: '12px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Cliente *</label>
                <button type="button" disabled={guardando} onClick={() => setNuevoCli((v) => !v)}
                  style={{ background: 'none', border: 'none', color: 'var(--color-primario,#DB2777)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                  {nuevoCli ? 'Cancelar' : '➕ Nuevo'}
                </button>
              </div>
              <select value={clienteId} disabled={guardando} onChange={(e) => setClienteId(e.target.value)} style={input}>
                <option value="">Sin cliente</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            {nuevoCli && (
              <div style={{ backgroundColor: '#FFF5F7', borderRadius: '8px', padding: '10px', border: '1px solid #FBCFE8', margin: '0 0 10px' }}>
                <input type="text" value={cliNombre} disabled={guardandoCli} autoFocus onChange={(e) => setCliNombre(e.target.value)} placeholder="Nombre *" style={{ ...input, marginBottom: '6px' }} />
                <input type="tel" value={cliTel} disabled={guardandoCli} onChange={(e) => setCliTel(e.target.value)} placeholder="Teléfono (opcional)" style={{ ...input, marginBottom: '8px' }} />
                <button type="button" onClick={guardarClienteRapido} disabled={guardandoCli}
                  style={{ width: '100%', padding: '9px', backgroundColor: guardandoCli ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                  {guardandoCli ? '⏳...' : 'Guardar y usar'}
                </button>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Canal de venta</label>
                <select value={canal} disabled={guardando} onChange={(e) => setCanal(e.target.value)} style={input}>
                  <option value="">Sin especificar</option>
                  {CANALES.map((c) => <option key={c.valor} value={c.valor}>{c.icono} {c.valor}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Fecha</label>
                <input type="date" value={fechaV} max={fechaLocal()} disabled={guardando} onChange={(e) => setFechaV(e.target.value)} style={input} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Descuento (L)</label>
                <CampoNumero valor={descuento} onChange={setDescuento} deshabilitado={guardando} min={0} max={subtotal} prefijo="L" placeholder="0.00" maxMensaje="El descuento no puede pasar del subtotal" estilo={input} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>🚚 Envío (L)</label>
                <CampoNumero valor={envio} onChange={setEnvio} deshabilitado={guardando} min={0} prefijo="L" placeholder="0.00" estilo={input} />
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Concepto</label>
              <input type="text" value={concepto} disabled={guardando} onChange={(e) => setConcepto(e.target.value)} style={input} placeholder="Ej: Apartado cartera" />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Nota</label>
              <input type="text" value={nota} disabled={guardando} onChange={(e) => setNota(e.target.value)} style={input} placeholder="Ej: Entregar el viernes" />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6B7280' }}>
              <span>Productos</span><span>{fmt(subtotal)}</span>
            </div>
            {desc > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#DC2626' }}><span>Descuento</span><span>−{fmt(desc)}</span></div>}
            {envioNum > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#0EA5E9' }}><span>🚚 Envío</span><span>+{fmt(envioNum)}</span></div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 'bold', margin: '6px 0 4px' }}>
              <span>Total deuda</span><span style={{ color: '#B45309' }}>{fmt(total)}</span>
            </div>
            {abonado > 0 && (
              <p style={{ margin: '0 0 12px', fontSize: '12px', color: 'var(--k-verde)' }}>Ya abonó {fmt(abonado)} · quedará debiendo {fmt(Math.max(total - abonado, 0))}</p>
            )}

            <button onClick={guardar} disabled={guardando || items.length === 0}
              style={{ width: '100%', padding: '13px', backgroundColor: (guardando || items.length === 0) ? '#9CA3AF' : '#B45309', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}>
              {guardando ? '⏳ Guardando...' : '💾 Guardar cambios'}
            </button>
            <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#9CA3AF', textAlign: 'center' }}>
              Los abonos ya hechos se conservan. Precios según el producto actual.
            </p>

            {confirmarBorrar ? (
              <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#FEE2E2', borderRadius: '8px', border: '1px solid #FCA5A5' }}>
                <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#991B1B', textAlign: 'center', fontWeight: 600 }}>
                  ¿Eliminar toda la venta? Se devuelve el stock y se revierten los abonos.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button onClick={() => setConfirmarBorrar(false)} disabled={guardando} style={{ padding: '9px', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>No, cancelar</button>
                  <button onClick={borrarVenta} disabled={guardando} style={{ padding: '9px', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>{guardando ? '⏳...' : 'Sí, eliminar'}</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setConfirmarBorrar(true)} disabled={guardando}
                style={{ width: '100%', marginTop: '8px', padding: '10px', background: 'white', color: '#DC2626', border: '1px solid #FCA5A5', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                🗑️ Eliminar toda la venta
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
